// src/encounter/lib/to-vitals-row.ts
// Map the backend PatientVital[] (GetVitalsDetailsByEncounter) → the DS VitalsRow
// `vitals` prop. Keyed by LOINC; BP combines systolic (8480-6) + diastolic (8462-4)
// into one "sys/dia" cell. Out-of-range = the backend `vitalNormalStatus` field
// ("abnormal") → DS-020 `warning` semantic; we do NOT recompute thresholds client-side.
import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';
import type { VitalsRowVitals, VitalReading } from '@scripsteam/scrips-signal-ds';

function codeOf(v: PatientVital): string | null {
  return v.code?.code ?? v.quantity?.code ?? null;
}

/**
 * UCUM wire unit → display unit (vitals-frame parity). The backend returns raw
 * UCUM codes ("Cel", "kg/m2"); the approved frame renders human units ("°C",
 * "kg/m²"). Unmapped units pass through unchanged — never invented.
 */
const UNIT_DISPLAY: Record<string, string> = {
  Cel: '°C',
  'kg/m2': 'kg/m²',
  '[degF]': '°F',
};

function displayUnit(unit: string | null | undefined): string | undefined {
  if (unit == null) return undefined;
  return UNIT_DISPLAY[unit] ?? unit;
}

function isAbnormal(v: PatientVital | undefined): boolean {
  return (v?.vitalNormalStatus ?? '').toLowerCase() === 'abnormal';
}

function isNormal(v: PatientVital | undefined): boolean {
  return (v?.vitalNormalStatus ?? '').toLowerCase() === 'normal';
}

function reading(v: PatientVital | undefined): VitalReading | undefined {
  const value = v?.quantity?.value;
  if (value === undefined || value === null || value === '') return undefined;
  return {
    value,
    unit: displayUnit(v?.quantity?.unit),
    semantic: isAbnormal(v) ? 'warning' : 'neutral',
    // D5 (Samer 2026-07-22, PROD-1588): carry the backend's EXPLICIT "normal"
    // classification as the DS interpretation — it feeds the vitals-strip NI chip.
    // A null/absent vitalNormalStatus stays uninterpreted (no fabricated normal claim);
    // we never recompute thresholds client-side.
    ...(isNormal(v) ? { interpretation: 'normal' as const } : {}),
  };
}

function numeric(v: PatientVital | undefined): number | undefined {
  const raw = v?.quantity?.value;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * BMI and Waist-to-Hip Ratio are DERIVED client-side, exactly as Flutter does:
 * `vital_data.dart:126-151` calls `calculateBmi` / `calculateWaistToHipRatio`
 * (`scrips_core/lib/utils/utils.dart:553-573`) instead of reading `quantity.value`.
 *
 * The backend returns the vital RECORD for these two codes — carrying
 * `vitalNormalStatus` — but routinely omits `quantity.value`, so `reading()` alone
 * drops both tiles to "—" while the Flutter app shows a value AND its status chip.
 * (Measured 2026-09-13 against dev-provider: Flutter renders BMI 34 and W:H 1.33
 * with a red "High" for a patient whose React tiles both read "—".)
 *
 * We derive the VALUE only. The interpretation still comes from the backend record,
 * so no threshold is recomputed client-side — the D5 rule above still holds.
 */
function derived(
  v: PatientVital | undefined,
  compute: () => string | undefined,
): VitalReading | undefined {
  const existing = reading(v);
  if (existing) return existing;
  const value = compute();
  if (value === undefined) return undefined;
  return {
    value,
    unit: displayUnit(v?.quantity?.unit),
    semantic: isAbnormal(v) ? 'warning' : 'neutral',
    ...(isNormal(v) ? { interpretation: 'normal' as const } : {}),
  };
}

/** `((weight * 10000) / (height * height)).roundToDouble()` — utils.dart:560, height in cm. */
export function bmiFrom(weightKg?: number, heightCm?: number): string | undefined {
  if (!weightKg || !heightCm) return undefined;
  return String(Math.round((weightKg * 10000) / (heightCm * heightCm)));
}

/** `waist / hip`, rendered at `displayForm(minDecimal: 2)` — utils.dart:565, vital_data.dart:58. */
export function waistToHipFrom(waistCm?: number, hipCm?: number): string | undefined {
  if (!waistCm || !hipCm) return undefined;
  return (waistCm / hipCm).toFixed(2);
}

/**
 * Visual-acuity fallback reading: `quantity.value` is null on VA rows persisted by the
 * dialog (PROD-1537 wire contract) — the reading lives in `extraData`. Derive the
 * distance OS/OD "20/dd · 20/dd" summary (Sc first, Cc fallback per eye). Nothing
 * derivable → undefined (no tile), never invented.
 */
export function visualAcuityReading(v: PatientVital): VitalReading | undefined {
  const d = v.extraData?.distance;
  if (!d) return undefined;
  const eye = (e: { sc?: { numerator?: string | null; denominator?: string | null } | null; cc?: { numerator?: string | null; denominator?: string | null } | null } | null | undefined) => {
    const p = e?.sc?.numerator && e?.sc?.denominator ? e.sc : e?.cc?.numerator && e?.cc?.denominator ? e.cc : null;
    return p ? `${p.numerator}/${p.denominator}` : null;
  };
  const os = eye(d.left_eye);
  const od = eye(d.right_eye);
  if (!os && !od) return undefined;
  return { value: [os, od].filter(Boolean).join(' · '), semantic: 'neutral' };
}

export interface PatientVitalsToRowOptions {
  /** Longitudinal BMI values (oldest→newest) → the BMI tile's inline sparkline
   *  (Figma VHvlfNWyFEymdjNpb6x45i 613:2118 "Edit Vitals"; parity loop 2). Source:
   *  useBmiHistory. Rendering depends on DS VitalsRow forwarding `sparkline` to
   *  VitalCard (DS fix pending — VitalReading declares it, 1.45.0 drops it). */
  bmiHistory?: number[];
}

/**
 * Reduce the bare PatientVital[] to a VitalsRowVitals. Latest-wins on duplicate
 * codes (the array is encounter-scoped and already current, but guard anyway).
 * BP renders "systolic/diastolic"; abnormal on either side flags the cell.
 */
export function patientVitalsToRow(
  vitals: PatientVital[] | undefined,
  opts?: PatientVitalsToRowOptions,
): VitalsRowVitals {
  if (!vitals?.length) return {};
  const byCode = new Map<string, PatientVital>();
  for (const v of vitals) {
    if (v.isDeleted) continue;
    const code = codeOf(v);
    if (code) byCode.set(code, v);
  }

  const out: VitalsRowVitals = {};

  const temp = reading(byCode.get(VITAL_CODES.TEMPERATURE));
  if (temp) out.temp = temp;
  const pulse = reading(byCode.get(VITAL_CODES.HEART_RATE));
  if (pulse) out.pulse = pulse;
  const respRate = reading(byCode.get(VITAL_CODES.RESP_RATE));
  if (respRate) out.respRate = respRate;
  const o2Sat = reading(byCode.get(VITAL_CODES.O2_SAT));
  if (o2Sat) out.o2Sat = o2Sat;
  const height = reading(byCode.get(VITAL_CODES.HEIGHT));
  if (height) out.height = height;
  const weight = reading(byCode.get(VITAL_CODES.WEIGHT));
  if (weight) out.weight = weight;
  // BMI tile carries the Figma 613:2118 extras: a green in-range check when the BACKEND
  // says the reading is normal (vitalNormalStatus — we never recompute thresholds
  // client-side) + the longitudinal sparkline when history is supplied.
  const bmiVital = byCode.get(VITAL_CODES.BMI);
  const bmi = derived(bmiVital, () =>
    bmiFrom(numeric(byCode.get(VITAL_CODES.WEIGHT)), numeric(byCode.get(VITAL_CODES.HEIGHT))),
  );
  if (bmi) {
    if (opts?.bmiHistory && opts.bmiHistory.length > 1) {
      // Trend takes the tile's trailing slot. The 184×49 VitalCard cannot fit value +
      // status chip + sparkline (value truncates — measured, parity loop 2); Figma's
      // compact dot+trend combo needs a smaller status affordance in VitalCard (DS
      // follow-up). Until then the trend wins; the check shows on trendless readings.
      bmi.sparkline = opts.bmiHistory;
    } else if ((bmiVital?.vitalNormalStatus ?? '').toLowerCase() === 'normal') {
      // Explicit backend "normal" only — a null status stays unmarked (no fabricated
      // in-range claim on a reading the backend didn't classify).
      bmi.semantic = 'success';
      bmi.badge = '✓';
    }
    out.bmi = bmi;
  }

  // Extended anthropometrics/screening (PROD-1533, Flutter emptyVitalUiList parity —
  // "the vitals are not all displayed" fix, Samer vitals review 2026-07-23): these codes
  // were fetched by GetVitalsDetailsByEncounter but never mapped, so recorded readings
  // (hip/waist/W:H/bioage/visual acuity/hearing) silently vanished from the strip. The
  // strip's EXTENDED_TILES render them only when a reading exists (presence: 'reading'),
  // so mapping them adds zero empty-tile bloat.
  const hipCirc = reading(byCode.get(VITAL_CODES.HIP));
  if (hipCirc) out.hipCirc = hipCirc;
  const waistCirc = reading(byCode.get(VITAL_CODES.WAIST));
  if (waistCirc) out.waistCirc = waistCirc;
  const waistToHipRatio = derived(byCode.get(VITAL_CODES.WAIST_TO_HIP_RATIO), () =>
    waistToHipFrom(numeric(byCode.get(VITAL_CODES.WAIST)), numeric(byCode.get(VITAL_CODES.HIP))),
  );
  if (waistToHipRatio) out.waistToHipRatio = waistToHipRatio;
  const bioage = reading(byCode.get(VITAL_CODES.BIOAGE));
  if (bioage) out.bioage = bioage;
  const hearingTest = reading(byCode.get(VITAL_CODES.HEARING));
  if (hearingTest) out.hearingTest = hearingTest;
  // Visual acuity persists its data in `extraData` with `quantity.value` often null
  // (the verbatim Flutter wire contract, PROD-1537) — derive the OS/OD distance-Sc
  // summary so a captured acuity surfaces WITH the vitals instead of only behind the
  // "+ Visual Acuity" affordance (Samer vitals review 2026-07-23).
  const vaVital = byCode.get(VITAL_CODES.VISUAL);
  if (vaVital) {
    const va = reading(vaVital) ?? visualAcuityReading(vaVital);
    if (va) out.visualAcuity = va;
  }

  // Context-aware vitals (PROD-1274). The DS VitalsRow renders headCirc only when
  // patientType='newborn' and lmp only when patientType='female', so mapping them
  // unconditionally is safe — they're gated downstream by the resolved patientType.
  // Head circumference: 11947-9 only, as Flutter (PROD-3185). Flutter saves it with no unit; it is in cm.
  const headCirc = reading(byCode.get(VITAL_CODES.HCM));
  if (headCirc) out.headCirc = { ...headCirc, unit: headCirc.unit || 'cm' };
  const lmp = reading(byCode.get(VITAL_CODES.LMP));
  if (lmp) out.lmp = lmp;

  // BP — combine systolic + diastolic into one cell.
  const sys = byCode.get(VITAL_CODES.SYSTOLIC);
  const dia = byCode.get(VITAL_CODES.DIASTOLIC);
  const sysVal = sys?.quantity?.value;
  const diaVal = dia?.quantity?.value;
  if (sysVal && diaVal) {
    out.bp = {
      value: `${sysVal}/${diaVal}`,
      semantic: isAbnormal(sys) || isAbnormal(dia) ? 'warning' : 'neutral',
      // D5: the composite cell is "normal" only when BOTH sides are explicitly
      // backend-classified normal — one unclassified side leaves it uninterpreted.
      ...(isNormal(sys) && isNormal(dia) ? { interpretation: 'normal' as const } : {}),
    };
  }

  return out;
}
